<template>
  <FlexLayout role="region">
    <vue-loading :active="isLoading" :is-full-page="true" />

    <BlockContainer v-if="isChild" :title="$t('customerCard.subTitle.info')" :aria-label="$t('customerCard.ariaLabel.info')">
      <h3 class="normalText"><strong>{{ $t('customerCard.label.holder') }}:</strong> {{ fullName(wondCustomer) }}</h3>
      <h3 class="normalText"><strong>{{ $t('customerCard.label.number') }}:</strong> {{ wondCustomer.cardNum }}</h3>
      <h3 class="normalText"><strong>{{ $t('customerCard.label.balance') }}</strong> {{ wondCustomer.formattedPrepayBalanceCash }}</h3>
    </BlockContainer>
    <BlockContainer :title="$t('autoTopUp.subTitle.configuration')" :aria-label="$t('autoTopUp.subTitle.configuration')">
      <ToggleButton
        id="toggleButton"
        v-model="active"
        :label="$t('autoTopUp.label.enable')"
        :aria-label="$t('autoTopUp.ariaLabel.enable')"
        required
      />
      <Slider id="thresholdAmount" name="thresholdAmount" :aria-label="$t('autoTopUp.ariaLabel.thresholdAmount')"
             v-model="thresholdAmount"
             :currency="currency"
             :currencyDisplay="'symbol'"
             :min="config.CUSTOMER_CARD.autoTopUp.thresholdAmount.minValue"
             :max="config.CUSTOMER_CARD.autoTopUp.thresholdAmount.maxValue"
             :step="1"
             :label="$t('autoTopUp.label.thresholdAmount')"
             required
      />
      <Slider id="reChargeAmount" name="reChargeAmount" :aria-label="$t('autoTopUp.ariaLabel.rechargeAmount')"
             v-model="rechargeAmount"
             :currency="currency"
             :currencyDisplay="'symbol'"
             :min="config.CUSTOMER_CARD.autoTopUp.rechargeAmount.minValue"
             :max="config.CUSTOMER_CARD.autoTopUp.rechargeAmount.maxValue"
             :step="1"
             :label="$t('autoTopUp.label.rechargeAmount')"
             required
      />
      <template #actions>
        <Button :disabled="rechargeAmount === null || thresholdAmount === null" @click.stop="confirm" :aria-label="$t('autoTopUp.ariaLabel.save')" variant="primary">{{ $t('autoTopUp.button.save', rechargeAmount, currency) }}</Button>
      </template>
    </BlockContainer>
  </FlexLayout>
</template>

<script>
import Utils from "@/utils/utils";
import VueLoading from "vue-loading-overlay";
import {accountService} from "@/services/api/accountService";
import {useAppStore} from "../../stores/app";
import {Workflow} from "../../constants/constants";
import {customerService} from "../../services/api/customerService";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import Button from "@/components/actions/Button.vue";
import Slider from "@/components/forms/Slider.vue";
import ToggleButton from "@/components/actions/ToggleButton.vue";

export default {
  name: "auto-top-up",
  components: {
    Button,
    FlexLayout,
    BlockContainer,
    Slider,
    ToggleButton,
    VueLoading,
  },
  data() {
    return {
      isLoading: false,
      wondCustomer: {},
      active: false,
      thresholdAmount: null,
      rechargeAmount: null,
      currencyCode: null,
    };
  },
  setup() {
    const appStore = useAppStore();

    return {
      appStore
    };
  },
  props: {
    customerId: {
      type: [String, Number],
    }
  },
  computed: {
    Utils() {
      return Utils
    },
    config(){
      return window.config;
    },
    isChild() {
      return this.appStore.isWorkflow(Workflow.SCHOOL);
    },
    currency() {
      return this.currencyCode || this.config.ACCOUNT_BALANCE_CURRENCY;
    },
  },
  methods: {
    fullName(customer) {
      const nameParts = [];

      if (customer.description) {
        nameParts.push(customer.description);
      }

      if (customer.firstName) {
        nameParts.push(customer.firstName);
      }

      return nameParts.join(' ').trim();
    },
    async selectChild(id) {
      this.isLoading = true;
      const result = await accountService.selectChild(id);
      this.isLoading = false;

      if (result.exception !== undefined || result.errorCode < 0) {
        this.makeToast('danger', this.$t('autoTopUp.messages.error.onSelectChild'));
        return;
      }

      return result;
    },
    async confirm() {
      this.isLoading = true;
      try {
        const result = await customerService.configureRecurringPayment(this.active, this.thresholdAmount, this.rechargeAmount);
        this.isLoading = false;

        if (result && result.basketTile && result.basketTile.currencyCode) {
          this.currencyCode = result.basketTile.currencyCode;
        }

        this.makeToast('success', this.$t('autoTopUp.messages.info.update'));
      } catch (error) {
        this.isLoading = false;
        // Show backend error message if available
        const message = error?.response?.data?.message || error.message || 'Unknown error occurred.';
        this.makeToast('danger', `Failed to update: ${message}`);
        console.error('Error in confirm:', error);
      }
    },
  },
  async mounted() {
    let result;
    if (this.isChild) {
      this.isLoading = true;
      result = await this.selectChild(this.customerId);
      this.isLoading = false;

      this.wondCustomer = result.wondChild;
    } else {
      result = await customerService.getCustomer();
    }

    if (result && result.basketTile && result.basketTile.currencyCode) {
      this.currencyCode = result.basketTile.currencyCode;
    }

    if (result.recurringPayment) {
      let recurringPaymentData = result.recurringPayment;
      // Ensure active is always Boolean
      this.active = recurringPaymentData.active !== undefined ? Boolean(recurringPaymentData.active) : false;
      this.thresholdAmount = recurringPaymentData.thresholdValue !== undefined ? recurringPaymentData.thresholdValue : undefined;
      this.rechargeAmount = recurringPaymentData.rechargeValue !== undefined ? recurringPaymentData.rechargeValue : undefined;
    }
  },
};
</script>
<style scoped>
/* Styles can be added here if needed */
</style>
